import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { auth } from '../firebase'; // Adjust path if firebase.js is elsewhere
import useApiClient from "../hooks/useApiClient";
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const getUmName = (umCode) => {
    // This should ideally come from a shared utility or constants file
    const umMap = {
        1: 'buc',
        2: 'kg',
        11: 'L',
        36: 'ora',
        // Add other mappings as present in your system
    };
    return umMap[umCode] || umCode; // Return code itself if not found
};

const formatDate = (dateString) => {
    if (!dateString) return '_________';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const formatCurrency = (amount) => {
    if (typeof amount !== 'number') return '0.00';
    return amount.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const PrintableInvoice = () => {
    const apiClient = useApiClient();
    const [searchParams] = useSearchParams();
    const [invoiceData, setInvoiceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const documentId = searchParams.get('id');
    const documentType = searchParams.get('type');

    useEffect(() => {
        const loadInvoiceData = async () => {
            if (!documentId || !documentType) {
                setError('ID document sau tip lipsă în URL.');
                setLoading(false);
                return;
            }
            if (!auth.currentUser) {
                setError("Utilizator neautentificat.");
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                let path = '';
                if (documentType === 'outgoing') {
                    path = `/invoice/outgoing/${documentId}`;
                } else if (documentType === 'incoming') {
                    path = `/invoice/received/${documentId}`;
                } else {
                    setError('Tip document invalid specificat în URL.');
                    setLoading(false);
                    return;
                }

                const responseData = await apiClient.get(path);
                console.log(responseData, 'RESPONSE');
                setInvoiceData(responseData)
                setError(null);
            } catch (err) {
                console.error('Error fetching invoice data:', err);
                setError(err.response?.data?.message || 'A apărut o eroare la încărcarea facturii.');
                setInvoiceData(null);
            }
            setLoading(false);
        };

        const unsubscribe = auth.onAuthStateChanged(user => {
            if (user) {
                loadInvoiceData();
            } else {
                setInvoiceData(null);
                setLoading(false);
                setError("Trebuie să fiți autentificat pentru a vedea factura.");
            }
        });
        return () => unsubscribe();

    }, [documentId, documentType, apiClient]);

    const processedLines = useMemo(() => {
        if (!invoiceData || !invoiceData.lines) return [];
        return invoiceData.lines.map((line, index) => ({
            nrCrt: index + 1,
            denumire: line.name,
            um: getUmName(line.um),
            cant: line.quantity,
            pretUnitar: line.unitPrice,
            vatRate: line.vatRate,
            vatLine: line.vatLine,
            valoare: line.lineTotal,
        }));
    }, [invoiceData]);

    // Calculate pages needed based on content
    const pagesNeeded = useMemo(() => {
        if (!processedLines.length) return 1;

        // Estimate rows per page (rough calculation)
        // Header + details take ~80mm, footer takes ~20mm, leaving ~197mm for content
        // Each table row is roughly 6mm, so ~32 rows per page
        const rowsPerPage = 25; // Conservative estimate
        const headerRowsCount = 3; // Header row + spacing
        const footerRowsCount = 3; // Total, tax, general total rows
        const availableRowsPerPage = rowsPerPage - headerRowsCount - footerRowsCount;

        return Math.max(1, Math.ceil(processedLines.length / availableRowsPerPage));
    }, [processedLines]);

    // Split lines across pages
    const pageContent = useMemo(() => {
        if (pagesNeeded === 1) {
            return [{
                lines: processedLines,
                showTotals: true,
                isLastPage: true
            }];
        }

        const pages = [];
        const rowsPerPage = Math.ceil(processedLines.length / pagesNeeded);

        for (let i = 0; i < pagesNeeded; i++) {
            const startIndex = i * rowsPerPage;
            const endIndex = Math.min(startIndex + rowsPerPage, processedLines.length);
            const isLastPage = i === pagesNeeded - 1;

            pages.push({
                lines: processedLines.slice(startIndex, endIndex),
                showTotals: isLastPage,
                isLastPage: isLastPage,
                pageNumber: i + 1
            });
        }

        return pages;
    }, [processedLines, pagesNeeded]);

    const downloadPDF = async () => {
        if (isDownloading) return;

        setIsDownloading(true);

        try {
            const pages = document.querySelectorAll('.page');
            if (!pages.length) {
                throw new Error('No pages found');
            }

            // Create PDF document
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true
            });

            // Process each page individually
            for (let i = 0; i < pages.length; i++) {
                const pageElement = pages[i];

                // Create canvas from individual page
                const canvas = await html2canvas(pageElement, {
                    scale: 2,
                    useCORS: true,
                    letterRendering: true,
                    allowTaint: true,
                    backgroundColor: '#ffffff',
                    width: pageElement.offsetWidth,
                    height: pageElement.offsetHeight,
                    windowWidth: pageElement.offsetWidth,
                    windowHeight: pageElement.offsetHeight
                });

                const imgData = canvas.toDataURL('image/jpeg', 0.95);

                // Add page to PDF (add new page for all except first)
                if (i > 0) {
                    doc.addPage();
                }

                // Add image to PDF page with proper A4 dimensions
                doc.addImage(imgData, 'JPEG', 0, 0, 210, 297);
            }

            // Generate filename
            const seriesNumber = invoiceData.seriesNumber || invoiceData.seriesNr || 'N-A';
            const dateStr = formatDate(invoiceData.issueDate).replace(/\//g, '-');
            const filename = `Factura_${seriesNumber}_${dateStr}.pdf`;

            // Save PDF
            doc.save(filename);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('A apărut o eroare la generarea PDF-ului. Vă rugăm să încercați din nou.');
        } finally {
            setIsDownloading(false);
        }
    };

    if (loading) {
        return <div className="p-8 text-center">Se încarcă datele facturii...</div>;
    }

    if (error) {
        return (
            <div className="p-8 text-center">
                <h1 className="text-xl font-bold text-red-600 mb-4">Eroare la încărcarea Facturii</h1>
                <p className="text-gray-700">{error}</p>
                <p className="text-gray-500 mt-2">Verificați consola pentru detalii sau reîncercați.</p>
            </div>
        );
    }

    if (!invoiceData) {
        return <div className="p-8 text-center">Nu s-au găsit date pentru factură.</div>;
    }

    const furnizor = invoiceData.seller;
    const client = invoiceData.buyer;


    return (
        <div className="printable-container text-gray-800 font-sans text-xs">
            <style>
                {`
                    body {
                        background-color: #f0f0f0;
                    }
                    .printable-container {
                        width: 210mm;
                        margin: 20px auto;
                        padding: 15mm;
                        box-shadow: 0 0 10px rgba(0,0,0,0.1);
                        border: 1px solid #ccc;
                        background: white;
                        max-height: 90vh;
                        overflow-y: auto;
                        overflow-x: hidden;
                    }
                    .document-content {
                        width: 100%;
                    }
                    .page-footer {
                        width: 100%;
                        background: #f8f9fa;
                        border-top: 2px solid #2c3e50;
                        border-bottom: 2px solid #2c3e50;
                        padding: 10px 15px;
                        box-sizing: border-box;
                        margin-top: 30px;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    @media print {
                        * {
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                            color-adjust: exact !important;
                        }
                        html, body {
                            margin: 0 !important;
                            padding: 0 !important;
                            background-color: #fff !important;
                            width: 100% !important;
                            height: auto !important;
                            overflow: visible !important;
                            position: static !important;
                        }
                        a[href]:after {
                            content: none !important;
                        }
                        .printable-container {
                            width: 100%;
                            margin: 0;
                            padding: 10mm;
                            box-shadow: none;
                            border: none;
                            max-height: none;
                            overflow: visible;
                        }
                        .no-print {
                            display: none !important;
                        }
                        .page-footer {
                            padding: 10px;
                            page-break-inside: avoid;
                            break-inside: avoid;
                        }
                        table {
                            page-break-inside: auto;
                        }
                        tbody tr {
                            page-break-inside: avoid;
                            break-inside: avoid;
                        }
                        tfoot {
                            display: table-row-group;
                            break-inside: avoid;
                            page-break-inside: avoid;
                        }
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 10px;
                    }
                    th, td {
                        padding: 4px 6px;
                        text-align: left;
                    }
                    .header-main {
                        background-color: #2c3e50;
                        color: white;
                        padding: 15px;
                        margin-bottom: 20px;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .header-main h1 {
                        font-size: 1.8em;
                        font-weight: bold;
                        margin:0;
                    }
                    .header-main p {
                        font-size: 0.9em;
                        margin:0;
                    }
                    .details-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                        gap: 20px;
                        margin-bottom: 20px;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .details-box {
                        border: 1px solid #333;
                        padding: 12px;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .details-box h3 {
                        font-weight: bold;
                        margin-bottom: 5px;
                        font-size: 0.9em;
                        text-transform: uppercase;
                    }
                    .items-table th {
                        border-bottom: 1px solid #555;
                        font-weight: bold;
                    }
                    .items-table td {
                        border-bottom: 1px dotted #ccc;
                    }
                    .items-table .text-right { text-align: right; }
                    .total-row td {
                        font-weight: bold;
                        border-top: 2px solid #333;
                        padding-top: 8px;
                    }
                    .items-table tbody tr {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .items-table tfoot {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                    .items-table tfoot tr {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                    .footer-notes {
                        margin-top: 20px;
                        font-size: 0.8em;
                        color: #555;
                    }
                    .section-title {
                        font-weight: bold;
                        text-transform: uppercase;
                        margin-top: 8px;
                        margin-bottom: 4px;
                        font-size: 0.9em;
                    }
                    .mb-4 {
                        margin-bottom: 16px;
                    }
                    .mb-6 {
                        margin-bottom: 24px;
                    }
                    .representative-footer {
                        margin: 0;
                        padding: 0;
                        background: transparent;
                        border: none;
                    }
                    .representative-footer h3 {
                        color: #2c3e50;
                        font-size: 0.8em;
                        font-weight: bold;
                    }
                    .representative-footer p {
                        margin: 2px 0;
                        font-size: 0.85em;
                        color:rgb(98, 98, 98);
                    }
                `}
            </style>

            <div className="no-print mb-6 flex gap-3">
                <button
                    onClick={() => window.print()}
                    className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                    Printează / Salvează PDF
                </button>
                <button
                    onClick={downloadPDF}
                    disabled={isDownloading}
                    className="p-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                    {isDownloading ? 'Se generează PDF...' : 'Descarcă ca PDF'}
                </button>
            </div>

            <div className="document-content">
                {pageContent.length > 0 && (
                    <>
                        <div className="header-main">
                            <h1>FACTURA</h1>
                            <p className='py-2'>Data: {formatDate(invoiceData.issueDate)}</p>
                            {invoiceData.seriesNumber && <p>Serie și număr: {invoiceData.seriesNumber}</p>}
                            {invoiceData.dueDate && <p>Termen plată: {formatDate(invoiceData.dueDate)}</p>}
                        </div>

                        <div className="details-grid">
                            <div className="details-box supplier-details">
                                <h3>FURNIZOR</h3>
                                <p><strong>{furnizor?.name}</strong></p>
                                {furnizor?.taxIdentifier && <p>CIF: {furnizor.taxIdentifier}</p>}
                                {furnizor?.reg_com && <p>Reg. com.: {furnizor.reg_com}</p>}
                                <p>Adresa: {furnizor?.address || '-'}</p>
                                <p>Oras: {furnizor?.city || '-'}</p>
                                <p>IBAN: {furnizor?.iban || '-'}</p>
                                <p>Banca: {furnizor?.bank || '-'}</p>
                            </div>
                            <div className="details-box client-details">
                                <h3>CLIENT</h3>
                                <p><strong>{client?.name}</strong></p>
                                {client?.taxIdentifier && <p>CIF: {client.taxIdentifier}</p>}
                                {client?.reg_com && <p>Reg. com.: {client.reg_com}</p>}
                                <p>Adresa: {client?.address || '-'}</p>
                                <p>Oras: {client?.city || '-'}</p>
                                {client?.subdivisionCode && <p>Judet: {client.subdivisionCode}</p>}
                                {client?.countryCode && <p>Tara: {client.countryCode}</p>}
                                <p>IBAN: {client?.iban || '-'}</p>
                                <p>Banca: {client?.bank || '-'}</p>
                            </div>
                        </div>
                    </>
                )}

                <h3 className="section-title">PRODUSE/SERVICII</h3>
                <table className="items-table mb-6">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Denumire</th>
                            <th className="text-right">U.M.</th>
                            <th className="text-right">Cant.</th>
                            <th className="text-right">Pret unitar (Lei)</th>
                            {invoiceData.taxAmount > 0 && (
                                <>
                                    <th className="text-right">Cota TVA (%)</th>
                                    <th className="text-right">TVA (Lei)</th>
                                </>
                            )}
                            <th className="text-right">Valoare (Lei)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {processedLines.map(line => (
                            <tr key={line.nrCrt}>
                                <td>{line.nrCrt}</td>
                                <td>{line.denumire}</td>
                                <td className="text-right">{line.um}</td>
                                <td className="text-right">{formatCurrency(line.cant)}</td>
                                <td className="text-right">{formatCurrency(line.pretUnitar)}</td>
                                {invoiceData.taxAmount > 0 && (
                                    <>
                                        <td className="text-right">{line.vatRate ? `${line.vatRate}%` : '-'}</td>
                                        <td className="text-right">{line.vatLine ? formatCurrency(line.vatLine) : '-'}</td>
                                    </>
                                )}
                                <td className="text-right">{formatCurrency(line.valoare)}</td>
                            </tr>
                        ))}
                        {processedLines.length === 0 && (
                            <tr><td colSpan={invoiceData.taxAmount > 0 ? "8" : "6"} className="text-center py-4">Nu sunt linii de factură.</td></tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="total-row">
                            <td colSpan={invoiceData.taxAmount > 0 ? "7" : "5"} className="text-right">Total factura:</td>
                            <td className="text-right">{formatCurrency(invoiceData.amount)} Lei</td>
                        </tr>
                        {invoiceData.taxAmount > 0 && (
                            <tr>
                                <td colSpan="7" className="text-right pt-1">Total TVA:</td>
                                <td className="text-right pt-1">{formatCurrency(invoiceData.taxAmount)} Lei</td>
                            </tr>
                        )}
                    </tfoot>
                </table>

                {/* Total General Section */}
                <div style={{
                    backgroundColor: '#f8f9fa',
                    border: '2px solid #2c3e50',
                    padding: '15px',
                    marginTop: '20px',
                    marginBottom: '20px',
                    textAlign: 'right',
                    fontWeight: 'bold',
                    fontSize: '1.1em',
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid'
                }}>
                    TOTAL GENERAL: {formatCurrency(invoiceData.totalAmount)} Lei
                </div>

                {invoiceData.notes && (
                    <div className="footer-notes mb-4">
                        <h4 className="font-bold">Observatii:</h4>
                        <p>{invoiceData.notes}</p>
                    </div>
                )}

                <div className="footer-notes">
                    <p>Factura este valabila fara semnatura si stampila, conform art. 319 alin. 29 din legea 227/2015.</p>
                </div>

                <div className="page-footer">
                    {invoiceData.representative && invoiceData.representative.name && (
                        <div className="representative-footer">
                            <div className="representative-info">
                                {invoiceData.representative.name && <p><strong>Intocmit de:</strong> {invoiceData.representative.name}</p>}
                                {invoiceData.representative.cnp && <p><strong>CNP:</strong> {invoiceData.representative.cnp} <strong>Buletin:</strong> {invoiceData.representative.buletin}</p>}
                            </div>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default PrintableInvoice;
